import {
  developmentIdentifier,
  type CandidateId,
  type CommitId,
  type RunId,
  type TaskId,
  type TreeId,
  type WorkspaceId
} from "@workerstation/shared";
import { boundedReviewDiffContents } from "@workerstation/shared";
import { stationPath, type StationPath } from "@workerstation/shared";

export const REVIEW_FILE_CHANGE_KINDS = [
  "added",
  "modified",
  "deleted",
  "renamed"
] as const;
export type ReviewFileChangeKind = (typeof REVIEW_FILE_CHANGE_KINDS)[number];

export const REVIEW_FILE_GROUP_IDS = [
  "conflicts",
  "updated-since-review",
  "unreviewed",
  "reviewed",
  "generated",
  "binary",
  "added",
  "deleted",
  "renamed"
] as const;
export type ReviewFileGroupId = (typeof REVIEW_FILE_GROUP_IDS)[number];

export interface ReviewChangedFileInput {
  readonly workspaceId: string;
  readonly path: string;
  readonly previousPath?: string;
  readonly changeKind: ReviewFileChangeKind;
  readonly conflictCount?: number;
  readonly updatedSinceReview?: boolean;
  readonly reviewed?: boolean;
  readonly generated?: boolean;
  readonly binary?: boolean;
  readonly additions?: number;
  readonly deletions?: number;
  readonly contributingTaskIds?: readonly string[];
  readonly contributingRunIds?: readonly string[];
}

export interface ReviewChangedFile {
  readonly workspaceId: WorkspaceId;
  readonly path: StationPath;
  readonly previousPath?: StationPath;
  readonly changeKind: ReviewFileChangeKind;
  readonly conflictCount: number;
  readonly updatedSinceReview: boolean;
  readonly reviewed: boolean;
  readonly generated: boolean;
  readonly binary: boolean;
  readonly additions: number;
  readonly deletions: number;
  readonly contributingTaskIds: readonly TaskId[];
  readonly contributingRunIds: readonly RunId[];
}

export interface ReviewFileGroup {
  readonly id: ReviewFileGroupId;
  readonly label: string;
  readonly files: readonly ReviewChangedFile[];
}

export interface ReviewFileGroups {
  /** Mutually exclusive review-state groups; every changed file appears once. */
  readonly attention: readonly ReviewFileGroup[];
  /** Orthogonal source-kind facets; a file can appear in more than one. */
  readonly kind: readonly ReviewFileGroup[];
  readonly total: number;
}

export interface ReviewFileDiffSnapshotInput {
  readonly treeId: string;
  readonly workspaceId: string;
  readonly path: string;
  readonly contents: string;
}

export interface ReviewFileDiffInput {
  readonly candidateId: string;
  readonly baseCommitId: string;
  readonly candidateCommitId: string;
  readonly original: ReviewFileDiffSnapshotInput;
  readonly modified: ReviewFileDiffSnapshotInput;
}

export interface ReviewFileDiffSnapshot {
  readonly treeId: TreeId;
  readonly workspaceId: WorkspaceId;
  readonly path: StationPath;
  readonly contents: string;
}

export interface ReviewFileDiff {
  readonly candidateId: CandidateId;
  readonly baseCommitId: CommitId;
  readonly candidateCommitId: CommitId;
  readonly original: ReviewFileDiffSnapshot;
  readonly modified: ReviewFileDiffSnapshot;
}

export interface ReviewCandidatePresentation {
  readonly candidateId: CandidateId;
  readonly currentCandidateId: CandidateId;
  readonly generation: number;
  readonly state: "current" | "superseded";
  readonly supersededByCandidateId?: CandidateId;
}

export type ReviewRouteIntegrity =
  | Readonly<{ status: "match" }>
  | Readonly<{
      status: "mismatch";
      reason: "change" | "candidate" | "missing-route-identity";
    }>;

export type ReviewRovingOrientation = "horizontal" | "vertical";

export type ReviewStatusTone =
  | "success"
  | "danger"
  | "warning"
  | "progress"
  | "neutral";

export interface ReviewStatusPresentation {
  readonly symbol: string;
  readonly label: string;
  readonly tone: ReviewStatusTone;
}

export interface ReviewAnnouncementInput {
  readonly routeMismatch?: boolean;
  readonly candidateState?: "current" | "superseded";
  readonly freshness?:
    | "current"
    | "target-moved"
    | "evidence-stale"
    | "authority-stale";
  readonly conflictCount?: number;
  readonly gateEnabled?: boolean;
  readonly blockerCount?: number;
  readonly failedCheckCount?: number;
}

const CHANGE_KIND_SET = new Set<string>(REVIEW_FILE_CHANGE_KINDS);
const MAX_CHANGED_FILES = 10_000;
const MAX_FILE_CONTRIBUTORS = 100;
const CHANGED_FILE_KEYS = new Set([
  "workspaceId",
  "path",
  "previousPath",
  "changeKind",
  "conflictCount",
  "updatedSinceReview",
  "reviewed",
  "generated",
  "binary",
  "additions",
  "deletions",
  "contributingTaskIds",
  "contributingRunIds"
]);
const CANDIDATE_PRESENTATION_KEYS = new Set([
  "candidateId",
  "currentCandidateId",
  "generation",
  "supersededByCandidateId",
  "state"
]);
const FILE_DIFF_KEYS = new Set([
  "candidateId",
  "baseCommitId",
  "candidateCommitId",
  "original",
  "modified"
]);
const FILE_DIFF_SNAPSHOT_KEYS = new Set([
  "treeId",
  "workspaceId",
  "path",
  "contents"
]);
const GROUP_LABELS: Readonly<Record<ReviewFileGroupId, string>> = Object.freeze({
  conflicts: "Conflicts",
  "updated-since-review": "Updated since review",
  unreviewed: "Unreviewed",
  reviewed: "Reviewed",
  generated: "Generated",
  binary: "Binary",
  added: "Added",
  deleted: "Deleted",
  renamed: "Renamed"
});

const SUCCESS_STATUSES = new Set([
  "current",
  "passed",
  "completed",
  "resolved",
  "addressed",
  "reviewed",
  "added"
]);
const DANGER_STATUSES = new Set([
  "failed",
  "error",
  "critical",
  "high",
  "conflict",
  "blocked",
  "timed-out",
  "deleted"
]);
const WARNING_STATUSES = new Set([
  "target-moved",
  "evidence-stale",
  "authority-stale",
  "superseded",
  "dismissed",
  "cancelled",
  "renamed",
  "medium",
  "stale"
]);
const PROGRESS_STATUSES = new Set([
  "queued",
  "running",
  "recovering",
  "pending",
  "draft",
  "open"
]);

/** APG-style roving focus for horizontal tabs and vertical trees. */
export function reviewRovingIndex(
  key: string,
  current: number,
  length: number,
  orientation: ReviewRovingOrientation
): number | undefined {
  if (
    !Number.isSafeInteger(current) ||
    !Number.isSafeInteger(length) ||
    current < 0 ||
    current >= length ||
    length < 1
  ) {
    return undefined;
  }
  if (key === "Home") return 0;
  if (key === "End") return length - 1;
  if (
    (orientation === "horizontal" && key === "ArrowRight") ||
    (orientation === "vertical" && key === "ArrowDown")
  ) {
    return (current + 1) % length;
  }
  if (
    (orientation === "horizontal" && key === "ArrowLeft") ||
    (orientation === "vertical" && key === "ArrowUp")
  ) {
    return (current - 1 + length) % length;
  }
  return undefined;
}

/** Pair every visual state cue with a stable, readable text label. */
export function reviewStatusPresentation(
  status: string,
  label = status.replaceAll("-", " ")
): ReviewStatusPresentation {
  const normalized = status.toLowerCase();
  const tone: ReviewStatusTone = SUCCESS_STATUSES.has(normalized)
    ? "success"
    : DANGER_STATUSES.has(normalized)
      ? "danger"
      : WARNING_STATUSES.has(normalized)
        ? "warning"
        : PROGRESS_STATUSES.has(normalized)
          ? "progress"
          : "neutral";
  const symbol = {
    success: "✓",
    danger: "×",
    warning: "!",
    progress: "…",
    neutral: "•"
  }[tone];
  return Object.freeze({ symbol, label, tone });
}

/** Announce only the newest durable milestone, never streaming progress. */
export function reviewMilestoneAnnouncement(input: {
  readonly activity?: readonly Readonly<{
    eventId: string;
    sequence: number;
    label: string;
  }>[];
  readonly specialists?: readonly Readonly<{
    role: string;
    latestMilestone?: string;
  }>[];
}): Readonly<{ key: string; message: string }> | undefined {
  const latest = [...(input.activity ?? [])]
    .filter(
      (event) =>
        Number.isSafeInteger(event.sequence) &&
        event.sequence >= 0 &&
        event.label.trim().length > 0
    )
    .sort(
      (left, right) =>
        right.sequence - left.sequence ||
        (left.eventId < right.eventId ? -1 : left.eventId > right.eventId ? 1 : 0)
    )[0];
  if (latest) {
    return Object.freeze({
      key: `${latest.sequence}:${latest.eventId}`,
      message: `Milestone ${latest.sequence}: ${latest.label}`
    });
  }
  const specialist = [...(input.specialists ?? [])]
    .reverse()
    .find((entry) => entry.latestMilestone?.trim());
  return specialist?.latestMilestone
    ? Object.freeze({
        key: `${specialist.role}:${specialist.latestMilestone}`,
        message: `${specialist.role} specialist milestone: ${specialist.latestMilestone}`
      })
    : undefined;
}

/** Build one concise assertive update for safety-relevant review state. */
export function reviewAssertiveAnnouncement(
  input: ReviewAnnouncementInput
): string | undefined {
  const messages: string[] = [];
  if (input.routeMismatch) {
    messages.push("Review route does not match the immutable candidate data.");
  }
  if (input.candidateState === "superseded") {
    messages.push("This candidate is superseded and read-only.");
  }
  if (input.freshness && input.freshness !== "current") {
    messages.push(`Candidate freshness changed: ${input.freshness.replaceAll("-", " ")}.`);
  }
  if ((input.conflictCount ?? 0) > 0) {
    const count = input.conflictCount!;
    messages.push(
      `${count} merge conflict${count === 1 ? "" : "s"} require${count === 1 ? "s" : ""} resolution.`
    );
  }
  if (input.gateEnabled === false) {
    const blockerCount = input.blockerCount ?? 0;
    messages.push(
      blockerCount > 0
        ? `Publication gate blocked by ${blockerCount} blocker${blockerCount === 1 ? "" : "s"}.`
        : "Publication gate is blocked."
    );
  }
  if ((input.failedCheckCount ?? 0) > 0) {
    const count = input.failedCheckCount!;
    messages.push(
      `${count} required check${count === 1 ? "" : "s"} failed.`
    );
  }
  return messages.length > 0 ? messages.join(" ") : undefined;
}

function inputRecord(
  value: unknown,
  allowedKeys: ReadonlySet<string>,
  description: string
): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${description} must be a plain object.`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${description} must be a plain object.`);
  }
  const record: Record<string, unknown> = Object.create(null);
  for (const key of Reflect.ownKeys(value)) {
    if (typeof key !== "string" || !allowedKeys.has(key)) {
      throw new TypeError(`${description} contains an unexpected field.`);
    }
    const descriptor = Object.getOwnPropertyDescriptor(value, key)!;
    if (!descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(
        `${description} fields must be enumerable data properties.`
      );
    }
    record[key] = descriptor.value;
  }
  return record;
}

function reviewFileDiffSnapshot(
  value: unknown,
  description: string
): ReviewFileDiffSnapshot {
  const input = inputRecord(value, FILE_DIFF_SNAPSHOT_KEYS, description);
  let contents: string;
  try {
    contents = boundedReviewDiffContents(input.contents);
  } catch {
    throw new TypeError(`${description} contents must be bounded text.`);
  }
  return Object.freeze({
    treeId: developmentIdentifier("tree", input.treeId),
    workspaceId: developmentIdentifier("workspace", input.workspaceId),
    path: stationPath(input.path),
    contents
  });
}

/**
 * Accept a lazy file diff only when every identity available to the Workbench
 * agrees with the immutable route selection. The UI never substitutes another
 * candidate, commit, tree, workspace, or path when a payload is stale.
 */
export function reviewFileDiffForSelection(input: Readonly<{
  candidateId: string;
  baseCommitId: string;
  candidateCommitId: string;
  candidateTreeId: string;
  workspaceId?: string;
  path?: string;
  previousPath?: string;
  binary: boolean;
  diff?: ReviewFileDiffInput;
}>): ReviewFileDiff | undefined {
  if (input.diff === undefined) return undefined;
  if (input.workspaceId === undefined || input.path === undefined) {
    throw new TypeError("A review diff requires an exact selected source path.");
  }
  if (input.binary) {
    throw new TypeError("A binary file cannot be mounted as a text review diff.");
  }
  const expectedCandidateId = developmentIdentifier(
    "candidate",
    input.candidateId
  );
  const expectedBaseCommitId = developmentIdentifier(
    "commit",
    input.baseCommitId
  );
  const expectedCandidateCommitId = developmentIdentifier(
    "commit",
    input.candidateCommitId
  );
  const expectedCandidateTreeId = developmentIdentifier(
    "tree",
    input.candidateTreeId
  );
  const expectedWorkspaceId = developmentIdentifier(
    "workspace",
    input.workspaceId
  );
  const expectedModifiedPath = stationPath(input.path);
  const expectedOriginalPath = stationPath(input.previousPath ?? input.path);
  const diff = inputRecord(input.diff, FILE_DIFF_KEYS, "Review file diff");
  const candidateId = developmentIdentifier("candidate", diff.candidateId);
  const baseCommitId = developmentIdentifier("commit", diff.baseCommitId);
  const candidateCommitId = developmentIdentifier(
    "commit",
    diff.candidateCommitId
  );
  const original = reviewFileDiffSnapshot(
    diff.original,
    "Review original snapshot"
  );
  const modified = reviewFileDiffSnapshot(
    diff.modified,
    "Review candidate snapshot"
  );

  if (candidateId !== expectedCandidateId) {
    throw new TypeError("Review diff candidate does not match the selected candidate.");
  }
  if (
    baseCommitId !== expectedBaseCommitId ||
    candidateCommitId !== expectedCandidateCommitId
  ) {
    throw new TypeError("Review diff commits do not match the selected candidate.");
  }
  if (modified.treeId !== expectedCandidateTreeId) {
    throw new TypeError("Review diff candidate tree does not match the selected candidate.");
  }
  if (
    original.workspaceId !== expectedWorkspaceId ||
    modified.workspaceId !== expectedWorkspaceId ||
    original.path !== expectedOriginalPath ||
    modified.path !== expectedModifiedPath
  ) {
    throw new TypeError("Review diff does not match the selected source path.");
  }
  return Object.freeze({
    candidateId,
    baseCommitId,
    candidateCommitId,
    original,
    modified
  });
}

function optionalBoolean(value: unknown, label: string): boolean {
  if (value === undefined) return false;
  if (typeof value !== "boolean") {
    throw new TypeError(`${label} must be a boolean.`);
  }
  return value;
}

function count(value: unknown, label: string): number {
  if (value === undefined) return 0;
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new TypeError(`${label} must be a non-negative safe integer.`);
  }
  return value as number;
}

function contributorIds(
  kind: "task",
  value: unknown,
  label: string
): readonly TaskId[];
function contributorIds(
  kind: "run",
  value: unknown,
  label: string
): readonly RunId[];
function contributorIds(
  kind: "task" | "run",
  value: unknown,
  label: string
): readonly (TaskId | RunId)[] {
  if (value === undefined) return Object.freeze([]);
  if (
    !Array.isArray(value) ||
    Object.getPrototypeOf(value) !== Array.prototype ||
    value.length > MAX_FILE_CONTRIBUTORS
  ) {
    throw new TypeError(`${label} must be a bounded plain array.`);
  }
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const ids = Array.from({ length: value.length }, (_, index) => {
    const descriptor = descriptors[index];
    if (!descriptor || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(`${label} must contain dense data properties.`);
    }
    return developmentIdentifier(kind, descriptor.value);
  }).sort(compareText);
  if (
    Reflect.ownKeys(value).some(
      (key) =>
        key !== "length" &&
        (typeof key !== "string" || !/^(?:0|[1-9][0-9]*)$/.test(key))
    ) ||
    new Set(ids).size !== ids.length
  ) {
    throw new TypeError(`${label} must contain unique plain identifiers.`);
  }
  return Object.freeze(ids);
}

function normalizeChangedFile(value: unknown): ReviewChangedFile {
  const input = inputRecord(
    value,
    CHANGED_FILE_KEYS,
    "Review changed file"
  );
  if (typeof input.changeKind !== "string" || !CHANGE_KIND_SET.has(input.changeKind)) {
    throw new TypeError("Review changed file kind is unsupported.");
  }
  const path = stationPath(input.path);
  const previousPath =
    input.previousPath === undefined
      ? undefined
      : stationPath(input.previousPath);
  if (input.changeKind === "renamed" && previousPath === undefined) {
    throw new TypeError("A renamed review file requires its previous path.");
  }
  if (input.changeKind !== "renamed" && previousPath !== undefined) {
    throw new TypeError("Only renamed review files may have a previous path.");
  }
  if (previousPath === path) {
    throw new TypeError("A renamed review file must change its path.");
  }
  return Object.freeze({
    workspaceId: developmentIdentifier("workspace", input.workspaceId),
    path,
    ...(previousPath === undefined ? {} : { previousPath }),
    changeKind: input.changeKind as ReviewFileChangeKind,
    conflictCount: count(input.conflictCount, "Review file conflict count"),
    updatedSinceReview: optionalBoolean(
      input.updatedSinceReview,
      "Review file updated-since-review flag"
    ),
    reviewed: optionalBoolean(input.reviewed, "Review file reviewed flag"),
    generated: optionalBoolean(input.generated, "Review file generated flag"),
    binary: optionalBoolean(input.binary, "Review file binary flag"),
    additions: count(input.additions, "Review file additions"),
    deletions: count(input.deletions, "Review file deletions"),
    contributingTaskIds: contributorIds(
      "task",
      input.contributingTaskIds,
      "Review file contributing tasks"
    ),
    contributingRunIds: contributorIds(
      "run",
      input.contributingRunIds,
      "Review file contributing runs"
    )
  });
}

function compareFiles(left: ReviewChangedFile, right: ReviewChangedFile): number {
  const workspace = compareText(left.workspaceId, right.workspaceId);
  return workspace === 0 ? compareText(left.path, right.path) : workspace;
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function group(
  id: ReviewFileGroupId,
  files: readonly ReviewChangedFile[]
): ReviewFileGroup {
  return Object.freeze({
    id,
    label: GROUP_LABELS[id],
    files: Object.freeze([...files])
  });
}

/**
 * Build deterministic explorer groups. Review-state groups are exclusive so
 * keyboard navigation never encounters duplicate source nodes; kind groups
 * are explicit secondary facets and may intentionally overlap.
 */
export function groupReviewChangedFiles(
  input: readonly ReviewChangedFileInput[]
): ReviewFileGroups {
  if (
    !Array.isArray(input) ||
    Object.getPrototypeOf(input) !== Array.prototype ||
    input.length > MAX_CHANGED_FILES
  ) {
    throw new TypeError("Review changed files must be a bounded plain array.");
  }
  const descriptors = Object.getOwnPropertyDescriptors(input);
  if (
    Reflect.ownKeys(input).some(
      (key) =>
        key !== "length" &&
        (typeof key !== "string" || !/^(?:0|[1-9][0-9]*)$/.test(key))
    )
  ) {
    throw new TypeError("Review changed files contain an unexpected field.");
  }
  const files: ReviewChangedFile[] = [];
  for (let index = 0; index < input.length; index += 1) {
    const descriptor = descriptors[index];
    if (!descriptor || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(
        "Review changed files must be a dense array of data properties."
      );
    }
    files.push(normalizeChangedFile(descriptor.value));
  }
  files.sort(compareFiles);
  const identities = files.map(
    (file) => `${file.workspaceId}\u0000${file.path}`
  );
  if (new Set(identities).size !== identities.length) {
    throw new TypeError("Review changed files must have unique station paths.");
  }

  const attention = new Map<ReviewFileGroupId, ReviewChangedFile[]>([
    ["conflicts", []],
    ["updated-since-review", []],
    ["unreviewed", []],
    ["reviewed", []]
  ]);
  const kind = new Map<ReviewFileGroupId, ReviewChangedFile[]>([
    ["generated", []],
    ["binary", []],
    ["added", []],
    ["deleted", []],
    ["renamed", []]
  ]);
  for (const file of files) {
    const attentionId: ReviewFileGroupId =
      file.conflictCount > 0
        ? "conflicts"
        : file.updatedSinceReview
          ? "updated-since-review"
          : file.reviewed
            ? "reviewed"
            : "unreviewed";
    attention.get(attentionId)!.push(file);
    if (file.generated) kind.get("generated")!.push(file);
    if (file.binary) kind.get("binary")!.push(file);
    if (file.changeKind !== "modified") {
      kind.get(file.changeKind)!.push(file);
    }
  }

  return Object.freeze({
    attention: Object.freeze(
      (["conflicts", "updated-since-review", "unreviewed", "reviewed"] as const).map(
        (id) => group(id, attention.get(id)!)
      )
    ),
    kind: Object.freeze(
      (["generated", "binary", "added", "deleted", "renamed"] as const).map(
        (id) => group(id, kind.get(id)!)
      )
    ),
    total: files.length
  });
}

export function reviewCandidatePresentation(input: {
  candidateId: string;
  currentCandidateId: string;
  generation: number;
  supersededByCandidateId?: string;
  state?: "current" | "superseded";
}): ReviewCandidatePresentation {
  const snapshot = inputRecord(
    input,
    CANDIDATE_PRESENTATION_KEYS,
    "Review candidate presentation"
  );
  if (!Number.isSafeInteger(snapshot.generation) || (snapshot.generation as number) < 1) {
    throw new TypeError("Candidate generation must be a positive safe integer.");
  }
  const candidateId = developmentIdentifier("candidate", snapshot.candidateId);
  const currentCandidateId = developmentIdentifier(
    "candidate",
    snapshot.currentCandidateId
  );
  const supersededByCandidateId =
    snapshot.supersededByCandidateId === undefined
      ? undefined
      : developmentIdentifier("candidate", snapshot.supersededByCandidateId);
  if (candidateId === currentCandidateId && supersededByCandidateId !== undefined) {
    throw new TypeError("The current candidate cannot be superseded.");
  }
  if (candidateId !== currentCandidateId && supersededByCandidateId === undefined) {
    throw new TypeError("A historical candidate must identify its successor.");
  }
  if (supersededByCandidateId === candidateId) {
    throw new TypeError("A historical candidate cannot supersede itself.");
  }
  const state = candidateId === currentCandidateId ? "current" : "superseded";
  if (snapshot.state !== undefined && snapshot.state !== state) {
    throw new TypeError("Review candidate state does not match its identities.");
  }
  return Object.freeze({
    candidateId,
    currentCandidateId,
    generation: snapshot.generation as number,
    state,
    ...(supersededByCandidateId === undefined
      ? {}
      : { supersededByCandidateId })
  });
}

/** Fail closed when a payload does not belong to the immutable route. */
export function reviewRouteIntegrity(input: {
  routeChangeId?: string;
  routeCandidateId?: string;
  dataChangeId: string;
  dataCandidateId: string;
}): ReviewRouteIntegrity {
  const routeChangeId =
    input.routeChangeId === undefined
      ? undefined
      : developmentIdentifier("change", input.routeChangeId);
  const routeCandidateId =
    input.routeCandidateId === undefined
      ? undefined
      : developmentIdentifier("candidate", input.routeCandidateId);
  const dataChangeId = developmentIdentifier("change", input.dataChangeId);
  const dataCandidateId = developmentIdentifier(
    "candidate",
    input.dataCandidateId
  );
  if (routeChangeId === undefined || routeCandidateId === undefined) {
    return Object.freeze({
      status: "mismatch",
      reason: "missing-route-identity"
    });
  }
  if (routeChangeId !== dataChangeId) {
    return Object.freeze({ status: "mismatch", reason: "change" });
  }
  if (routeCandidateId !== dataCandidateId) {
    return Object.freeze({ status: "mismatch", reason: "candidate" });
  }
  return Object.freeze({ status: "match" });
}
